import React, { useState, useEffect, useMemo } from "react";
import { Button, Table, Spinner, Badge } from "react-bootstrap";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useNavigate } from "react-router-dom";
import toastr from "toastr";

const PageList_GoodsTypeMaster = () => {
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const API_URL = `${API_WEB_URLS.MASTER}/0/token/GoodsTypeMaster`;

  const fetchTableData = async () => {
    setLoading(true);
    try {
      await Fn_FillListData(dispatch, setGridData, "gridData", `${API_URL}/Id/0`);
    } catch (err) {
      console.error("Failed to load Goods Types:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, [dispatch, API_URL]);

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete Goods Type "${name || id}"?`)) {
      try {
        const res = await Fn_DeleteData(dispatch, id, API_URL, `${API_URL}/Id/0`);
        if (res?.success) {
          toastr.success(`Goods Type "${name}" deleted successfully`);
          fetchTableData();
        }
      } catch (err) {
        console.error("Delete error:", err);
        toastr.error("Failed to delete Goods Type");
      }
    }
  };

  const data = useMemo(() => {
    if (!gridData || !Array.isArray(gridData)) return [];
    return [...gridData].sort((a, b) => {
      const orderA = Number(a.DisplayOrder ?? 0);
      const orderB = Number(b.DisplayOrder ?? 0);
      if (orderA !== orderB) return orderA - orderB;
      return (a.Name || "").localeCompare(b.Name || "");
    });
  }, [gridData]);

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "Id",
        Cell: ({ value }) => (
          <span className="text-muted small fw-bold font-monospace">#{value || "-"}</span>
        ),
      },
      {
        Header: "Icon",
        accessor: "Icon",
        Cell: ({ value }) => (
          <span style={{ fontSize: "1.3rem", display: "inline-block", minWidth: 28, textAlign: "center" }}>
            {value || "📦"}
          </span>
        ),
      },
      {
        Header: "Goods Type Name",
        accessor: "Name",
        Cell: ({ row }) => (
          <div>
            <span className="fw-semibold text-dark" style={{ fontSize: 13 }}>
              {row.original.Name || "-"}
            </span>
            {row.original.Description && (
              <small className="text-muted d-block" style={{ fontSize: 11 }}>
                {row.original.Description}
              </small>
            )}
          </div>
        ),
      },
      {
        Header: "Order",
        accessor: "DisplayOrder",
        Cell: ({ value }) => (
          <span className="badge bg-light text-dark border font-monospace">
            {value != null ? value : 0}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "IsActive",
        Cell: ({ value }) => (
          <Badge bg={value === false || value === 0 ? "danger" : "success"} className="px-2 py-1">
            {value === false || value === 0 ? "Inactive" : "Active"}
          </Badge>
        ),
      },
      {
        Header: "Created On",
        accessor: "DateOfCreation",
        Cell: ({ value }) => (
          <span className="text-muted small">
            {value ? String(value).split("T")[0] : "-"}
          </span>
        ),
      },
      {
        id: "actions",
        Header: "Actions",
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: 6 }}>
            <Button
              size="sm"
              variant="outline-primary"
              className="py-0.5 px-2"
              style={{ fontSize: 11 }}
              onClick={() => navigate("/AddGoodsTypeMaster", { state: { Id: row.original.Id } })}
              title="Edit Goods Type"
            >
              <i className="fas fa-edit me-1"></i>Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              className="py-0.5 px-2"
              style={{ fontSize: 11 }}
              onClick={() => handleDelete(row.original.Id, row.original.Name)}
              title="Delete Goods Type"
            >
              <i className="fas fa-trash-alt me-1"></i>Del
            </Button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 15 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loading) {
    return (
      <div style={{ padding: "12px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 160 }}>
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading Goods Types...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 12px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header Banner */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: "1.5px solid #dee2e6",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#fff3cd",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.1rem",
            }}
          >
            📦
          </span>
          <div>
            <h6 className="mb-0 fw-bold text-dark">Goods Type Master</h6>
            <small className="text-muted">Manage goods/cargo options selectable by customers during booking</small>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="text"
            placeholder="Search Goods Types..."
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: 200, height: 30, fontSize: 12 }}
          />
          <Button
            size="sm"
            variant="primary"
            className="py-1 px-3 fw-semibold d-flex align-items-center gap-1"
            style={{ fontSize: 12, height: 30 }}
            onClick={() => navigate("/AddGoodsTypeMaster", { state: { Id: 0 } })}
          >
            <i className="fas fa-plus font-size-11"></i>
            <span>Add Goods Type</span>
          </Button>
        </div>
      </div>

      {/* Grid Table */}
      <div style={{ overflowX: "auto", marginBottom: 8 }} className="shadow-sm rounded border">
        <Table {...getTableProps()} size="sm" bordered hover className="mb-0" style={{ fontSize: 12, minWidth: 650 }}>
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map((col, j) => (
                  <th
                    {...col.getHeaderProps(col.getSortByToggleProps())}
                    key={j}
                    style={{
                      padding: "8px 10px",
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor: "#E87B2D",
                      color: "#fff",
                      verticalAlign: "middle",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <span>{col.render("Header")}</span>
                      <span>
                        {col.isSorted ? (col.isSortedDesc ? " ▼" : " ▲") : ""}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4 text-muted">
                  No Goods Types found. Click <strong>+ Add Goods Type</strong> to create one.
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i} className="align-middle">
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j} style={{ padding: "6px 10px", verticalAlign: "middle" }}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination Controls */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, flexWrap: "wrap", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="text-muted">
            Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length || 1}</strong> ({data.length} total records)
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: 90, height: 28, fontSize: 11 }}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[10, 15, 20, 30, 50].map((n) => (
              <option key={n} value={n}>
                Show {n}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex gap-1">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary py-0.5 px-2"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            |«
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary py-0.5 px-2"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            ‹
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary py-0.5 px-2"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            ›
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary py-0.5 px-2"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
          >
            »|
          </button>
        </div>
      </div>
    </div>
  );
};

export default PageList_GoodsTypeMaster;
